// Finance reconciliation contracts (ADR 0011), mirrored from GET
// /v1/commission/reconciliation. Money is rendered as the API's exact decimal
// strings — never floated. The exceptions surface is what makes leakage visible.
import type { MessageKey } from "../../i18n/dictionaries";

export type EntryStatus = "EXPECTED" | "RECONCILED" | "VARIANCE" | "DISPUTED" | "CLAWED_BACK";

// i18n keys for an exception class's Turkish DISPLAY label + its short tooltip.
// The API enum VALUES never change here — only what the advisor sees. Tolerant of
// the extra non-enum classes the read carries ("UNMATCHED", "MISSING_RATE").
export const statusLabelKey = (s: string): MessageKey => `recon.status.${s}` as MessageKey;
export const statusHelpKey = (s: string): MessageKey => `recon.statusHelp.${s}` as MessageKey;

export interface StatusCounts {
  EXPECTED: number;
  RECONCILED: number;
  VARIANCE: number;
  DISPUTED: number;
  CLAWED_BACK: number;
}

export interface CarrierRow {
  carrierId: string;
  carrierCode: string;
  entryCount: number;
  expected: string;
  received: string;
  gap: string;
  statusCounts: StatusCounts;
}

export interface ExceptionEntry {
  entryId: string;
  policyId: string;
  carrierId: string;
  expected: string;
  received: string;
  gap: string;
  ownerAdvisorId: string;
}

export interface UnmatchedLine {
  lineId: string;
  statementId: string;
  carrierId: string;
  carrierReference: string;
  amount: string;
  lineType: string;
}

export interface MissingRateItem {
  policyId: string;
  carrierId: string;
  carrierCode: string;
  carrierPolicyNumber: string;
  lineCode: string;
  termStart: string;
}

export interface Reconciliation {
  totals: {
    expected: string;
    received: string;
    gap: string;
    entryCount: number;
    statusCounts: StatusCounts;
  };
  byCarrier: CarrierRow[];
  exceptions: {
    variances: { count: number; items: ExceptionEntry[] };
    disputed: { count: number; items: ExceptionEntry[] };
    clawedBack: { count: number; items: ExceptionEntry[] };
    unmatchedLines: { count: number; items: UnmatchedLine[] };
    missingRate: { count: number; items: MissingRateItem[] };
  };
}

export interface ReconciliationFilters {
  carrierId?: string;
  periodStart?: string;
  periodEnd?: string;
  status?: EntryStatus;
}

export interface DisputeInput {
  action: "open" | "resolve";
  reason?: string;
  resolution?: "RECONCILED" | "VARIANCE";
}

export const LINE_TYPES = ["PAYMENT", "CLAWBACK"] as const;
export type LineType = (typeof LINE_TYPES)[number];

export interface StatementLineInput {
  carrierReference: string;
  lineType: LineType;
  amount: string;
}

export interface StatementInput {
  carrierId: string;
  periodStart: string;
  periodEnd: string;
  statementTotal: string;
  lines: StatementLineInput[];
}

// Bulk statement entry — a lump-sum total + the covered carrier_references; the
// server allocates proportionally to each covered entry's Expected (the
// remainder-to-the-cent + is_estimated discipline is server-side, CO3).
export interface BulkStatementInput {
  carrierId: string;
  periodStart: string;
  periodEnd: string;
  statementTotal: string;
  coveredReferences: string[];
}

/** The matcher's result (StatementResult) — per-entry verdicts the server computed. */
export interface StatementResult {
  statementId: string;
  status: string;
  lines: Array<{
    id: string;
    matched: boolean;
    entryStatus: string | null;
    variance: string | null;
  }>;
}
